import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Pencil } from "lucide-react";

function useBilling() {
  return useQuery({ queryKey: ["billing"], queryFn: async () => (await api.get("/account/billing")).data });
}

export function computeStatus(renewal) {
  if (!renewal) return { level: "unset", label: "Set your renewal date", color: "bg-navy-300" };
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = new Date(renewal + "T00:00:00");
  const days = Math.ceil((d - today) / 86400000);
  if (isNaN(days)) return { level: "unset", label: "Set your renewal date", color: "bg-navy-300" };
  if (days < 0) return { level: "red", label: `Payment overdue by ${-days} day${-days === 1 ? "" : "s"}`, color: "bg-red-500", days };
  if (days <= 7) return { level: "amber", label: `Renews in ${days} day${days === 1 ? "" : "s"}`, color: "bg-amber-500", days };
  return { level: "green", label: `Active · ${days} days left`, color: "bg-emerald-500", days };
}

export function BillingDot() {
  const { data } = useBilling();
  const s = computeStatus(data?.renewal_date);
  return (
    <div className="mt-2 flex items-center gap-2" data-testid="billing-dot">
      <span className={`h-2.5 w-2.5 rounded-full ${s.color}`} />
      <span className="text-[11px] text-navy-500">{s.label}</span>
    </div>
  );
}

const lights = [
  { level: "red", on: "bg-red-500", off: "bg-red-500/15" },
  { level: "amber", on: "bg-amber-500", off: "bg-amber-500/15" },
  { level: "green", on: "bg-emerald-500", off: "bg-emerald-500/15" },
];

export default function BillingStatus() {
  const qc = useQueryClient();
  const { data } = useBilling();
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState("");
  const [note, setNote] = useState("");
  const s = computeStatus(data?.renewal_date);

  useEffect(() => { if (data) { setDate(data.renewal_date || ""); setNote(data.note || ""); } }, [data]);

  const save = async () => {
    await api.post("/account/billing", { renewal_date: date, note });
    qc.invalidateQueries({ queryKey: ["billing"] });
    toast.success("Billing reminder saved");
    setOpen(false);
  };

  return (
    <Card data-testid="billing-status" className="mb-6 flex flex-col gap-3 rounded-2xl border-navy-900/5 bg-white p-5 shadow-ambient sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-4">
        <div className="flex flex-col gap-1.5 rounded-xl bg-navy-900 p-2.5">
          {lights.map((l) => (
            <span key={l.level} className={`h-3 w-3 rounded-full transition-colors ${s.level === l.level ? l.on : l.off}`} />
          ))}
        </div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-navy-400">Emergent Account</div>
          <div className="font-semibold text-navy-900">{s.label}</div>
          <div className="text-xs text-navy-400">{data?.note || "Manual reminder — keeps your subscription top of mind"}</div>
        </div>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button data-testid="billing-edit" variant="outline" className="rounded-full border-navy-100">
            <Pencil className="mr-2 h-3.5 w-3.5" /> Update reminder
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-navy-900">Account &amp; billing reminder</DialogTitle>
            <DialogDescription className="text-navy-500">
              A private reminder for your Emergent subscription renewal. This is a manual reminder you control — not a live billing feed.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Next renewal / payment date</Label>
              <Input type="date" data-testid="billing-date-input" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1 focus-visible:ring-gold-500" />
            </div>
            <div>
              <Label>Note (optional)</Label>
              <Input data-testid="billing-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Auto top-up enabled on Universal Key" className="mt-1 focus-visible:ring-gold-500" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
            <Button data-testid="billing-save" onClick={save} className="rounded-full bg-gold-500 text-white hover:bg-gold-600">Save reminder</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
